import React, { Component } from 'react';
import ReactLoading from 'react-loading';
import styledComponent, { css } from 'styled-components';
import drop from 'lodash/drop';
import RadioButtonList from '../../components/Common/RadioButtonList';
import SlotLabel from './SlotLabel';
import Message from '../../components/Alert/message';
import { dateFromParams, formatStrartToEndClassDate } from '../../utilities/timeUtility';
import { getIntOrDefault } from '../../utilities/conversionUtility';
import dayjs from '../../../dayjs/dayjs_timezone';
import { uniqTimeslots } from '../../utilities/helperUtility';

const StyledPaginator = styledComponent.div`
  ${({ styled }) => {
    const bc = styled && styled.button ? styled.button.backgroundColor : '#C4CDD5';
    const ic = styled && styled.inactive ? styled.inactive.backgroundColor : '#E3E7EB';

    return css`
      && {
        display: flex;
        justify-content: center;
      
        button {
          background: none;
          border-width: 0;
        }
        
        .arrow {
          cursor: pointer;
          
          &.disabled {
            cursor: auto;
            svg {
              fill: ${ic} !important; 
            }
          }
          
          svg {
            width: 36px;
            fill: ${bc};
          }
                  
          &.left-arrow {
            margin-right: 10px;
          }
          
          &.right-arrow {
            margin-left: 10px;
          }
        }
      }
    `;
  }};
`;

const timeslotFromParams = (props, slots) => {
  const {
    timeZone,
    urlParams,
  } = props;

  const date = dateFromParams(urlParams, timeZone);
  if (!date) return null;
  return slots.find((x) => dayjs(x.start).isSame(date));
};

class Index extends Component {
  constructor(props) {
    super(props);

    this.radioButtonsRef = React.createRef();

    this.state = {
      slots: [],
      currentPage: 1,
      minHeight: 0,
    };
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      frames,
      getAvailability,
      onSlotSelect,
      setting,
      setUrlParam,
      timeZone,
      variant,
    } = this.props;
    const { currentPage } = this.state;
    const isSettingChanged = JSON.stringify(newProps.setting) !== JSON.stringify(setting);
    const isFramesChanged = JSON.stringify(newProps.frames) !== JSON.stringify(frames);
    const isTimeZoneChanged = newProps.timeZone !== timeZone;
    const isVariantChanged = JSON.stringify(newProps.variant) !== JSON.stringify(variant);
    const isPageSizeChanged = this.pageSize(newProps) !== this.pageSize();
    let slots = [];
    let firstAvailable;
    if (isFramesChanged || isSettingChanged || isTimeZoneChanged || isVariantChanged) {
      newProps.frames.forEach((frame) => {
        frame.timeslots.forEach((slot) => {
          const { start, finish } = slot;
          const labelArr = formatStrartToEndClassDate(
            start,
            finish,
            newProps.locale,
            newProps.timeZone,
            newProps.setting.time_format,
            newProps.setting.date_format,
            newProps.region,
          );
          const label = <>{labelArr.map((el) => <div key={el}>{el}</div>)}</>;
          const available = getAvailability(slot);
          const newSlot = {
            ...slot,
            ...{
              label,
              available,
              date: frame.date,
            },
          };

          if (!firstAvailable && available) firstAvailable = newSlot;

          slots.push(newSlot);
        });
      });

      let newCurrentPage = isPageSizeChanged ? 1 : currentPage;

      slots = uniqTimeslots(slots);

      const timeslot = timeslotFromParams(newProps, slots);
      if (timeslot) {
        const timeslotIndex = slots.findIndex((s) => s.id === timeslot.id) + 1;
        newCurrentPage = Math.ceil(timeslotIndex / this.pageSize());
        onSlotSelect(timeslot);
        setUrlParam && setUrlParam('start', null);
      } else if (firstAvailable && (!newProps.selectedTimeslot.id || isVariantChanged)) {
        const firstAvailableIndex = slots.findIndex((s) => s.id === firstAvailable.id) + 1;
        newCurrentPage = Math.ceil(firstAvailableIndex / this.pageSize());
        onSlotSelect(firstAvailable);
      }

      this.setState({ slots, currentPage: newCurrentPage });
    }
  }

  pageSize(props = this.props) {
    const { setting: { page_size: pageSize } } = props;
    const size = getIntOrDefault(pageSize, 8);
    return size > 8 ? 8 : size;
  }

  radioExtraContent(option) {
    const {
      setting: {
        low_capacity_threshold: lowCapacityThreshold,
      },
      translation,
      quantity,
      variant,
    } = this.props;
    let { available } = option;
    const { availability } = option;
    const lowCapacity = getIntOrDefault(lowCapacityThreshold, 4);
    const unitMultiplier = variant?.unit_multiplier || 1;
    const total = parseInt(quantity / unitMultiplier, 10);
    const variantAvailability = variant && availability.find((x) => x.variant_id === variant.id);
    const details = variantAvailability && variantAvailability.status_detail;

    available = parseInt(available / unitMultiplier, 10);

    let label = translation.key('available').replace('{{available}}', available);

    if (available <= 0 || available < total) {
      label = translation.key('bookedOut');
    } else if (available === 1) {
      label = translation.key('lastSeat');
    } else if (available < lowCapacity) {
      label = translation.key('partiallyAvailable').replace('{{available}}', available);
    }

    return (
      <div
        className="radio-extra-content"
        style={{ width: '35%', margin: 'auto 0 auto 5px' }}
      >
        <SlotLabel option={{ label, details }} />
      </div>
    );
  }

  renderMessage() {
    const { loading, styled, translation } = this.props;

    if (loading) {
      return (
        <ReactLoading
          type="spin"
          color={styled.body ? styled.body.color : null}
          height={75}
          width={75}
          className="react-loading"
        />
      );
    }

    const m = translation.key('noScheduledEvents');
    return (
      <div className="text-center">
        <Message message={m} type="success" customStyle={styled.body} />
      </div>
    );
  }

  goToPage(disabled, next) {
    if (disabled) return;
    const { onSlotSelect } = this.props;
    const { currentPage } = this.state;

    this.setState({
      currentPage: currentPage + (next ? 1 : -1),
      minHeight: this.radioButtonsRef.current.clientHeight,
    });

    onSlotSelect();
  }

  renderContent() {
    const {
      onSlotSelect, selectedTimeslot, styled,
    } = this.props;
    const { currentPage, minHeight, slots } = this.state;
    const pgSize = this.pageSize();
    const isFirstPage = currentPage <= 1;
    const isLastPage = currentPage >= Math.ceil(slots.length / pgSize);
    const offset = (currentPage - 1) * pgSize;
    const pagedItems = drop(slots, offset).slice(0, pgSize);

    return (
      <div style={{ marginTop: '20px' }}>
        <div ref={this.radioButtonsRef} style={{ minHeight }}>
          <RadioButtonList
            name="timeSlots"
            labelClass="radio-button-label"
            styled={styled.button}
            value={selectedTimeslot.id}
            options={pagedItems}
            onChange={(value) => { onSlotSelect(slots.find((x) => x.id === +value)); }}
            renderButton={(option) => this.radioExtraContent(option)}
          />
        </div>

        {(!isFirstPage || !isLastPage)
          && (
          <StyledPaginator styled={styled}>
            <button
              type="button"
              className={`arrow left-arrow ${isFirstPage ? 'disabled' : ''}`}
              disabled={isFirstPage}
              onClick={() => { this.goToPage(isFirstPage, false); }}
            >
              <svg viewBox="0 0 20 20">
                <path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm1.707 4.293a1 1 0 0 1 0 1.414L9.414 10l2.293 2.293a1 1 0 0 1-1.414 1.414l-3-3a1 1 0 0 1 0-1.414l3-3a1 1 0 0 1 1.414 0z" />
              </svg>
            </button>

            <button
              type="button"
              className={`arrow right-arrow ${isLastPage ? 'disabled' : ''}`}
              disabled={isLastPage}
              onClick={() => { this.goToPage(isLastPage, true); }}
            >
              <svg viewBox="0 0 20 20">
                <path d="M10 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zM8.293 6.293a1 1 0 0 0 0 1.414L10.586 10l-2.293 2.293a1 1 0 1 0 1.414 1.414l3-3a1 1 0 0 0 0-1.414l-3-3a1 1 0 0 0-1.414 0z" />
              </svg>
            </button>
          </StyledPaginator>
          )}
      </div>
    );
  }

  render() {
    const { slots } = this.state;
    const { loading } = this.props;

    // Show spinner if loading, regardless of slots
    if (loading) {
      return this.renderMessage();
    }

    return slots.length ? this.renderContent() : this.renderMessage();
  }
}

export default Index;
